import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Browser } from '@capacitor/browser'

import { Modal } from '@components/Modal'

import { useModal } from '@hooks/app/modal.hook'
import { useSignOut } from '@hooks/auth/sign-out.hook'

import ArrowRightIcon from '@assets/images/arrow-right-icon.svg?react'

import { ProfileInfos } from '../../components/ProfileInfos'

import {
  ItemWrapper,
  ItemContainer,
  ItemText,
  ItemIcon,
  Divider,
  LogoutButton,
  SectionContent,
  SectionTitle,
  SectionsContainer,
  Section,
  Container,
} from './mobile.settings.styled'

interface SettingsItem {
  id: string
  title: string
  onClick: () => void
}

interface NavigationItem {
  id: string
  title: string
  onClick?: () => void
  route?: string
}

interface NavigationSection {
  id: string
  title: string
  items: NavigationItem[]
}

interface NavigationPageProps {
  onBack: () => void
}

interface NavigationPage {
  id: string
  title: string
  component: React.ComponentType<NavigationPageProps>
}

export const MobileSettingsPage = (): JSX.Element => {
  const { hideModal } = useModal()
  const { signOut } = useSignOut()
  const { t } = useTranslation()

  const [currentRoute, setCurrentRoute] = useState<string>('settings')

  const pages: Record<string, NavigationPage> = {
    profile: {
      id: 'profile',
      title: t('modules_settings_pages_title_my-account'),
      component: ProfileInfos,
    },
  }

  const sections: NavigationSection[] = [
    {
      id: 'account',
      title: t('modules_settings_pages_title_info'),
      items: [
        {
          id: 'profile',
          title: t('modules_settings_pages_title_my-account'),
          route: 'profile',
        },
      ],
    },
    {
      id: 'legal',
      title: t('modules_settings_pages_title_doc'),
      items: [
        {
          id: 'legal-docs',
          title: t('modules_settings_pages_title_legal'),
          onClick: () => Browser.open({ url: 'https://www.toolkit.ac/mentions-legales' }),
        },
      ],
    },
  ]

  const navigateTo = (route: string): void => {
    setCurrentRoute(route)
  }

  const handleBack = (): void => {
    setCurrentRoute('settings')
  }

  const handleLogout = async (): Promise<void> => {
    signOut()
    hideModal('settings')
  }

  const SettingsContent = (): JSX.Element => (
    <Container>
      <SectionsContainer>
        {sections.map((section) => (
          <Section key={section.id}>
            <SectionTitle>{section.title}</SectionTitle>
            <SectionContent>
              {section.items.map((item: NavigationItem, index: number) => (
                <SettingsItem
                  key={item.id}
                  item={item}
                  isLast={index === section.items.length - 1}
                  onNavigate={navigateTo}
                />
              ))}
            </SectionContent>
          </Section>
        ))}
      </SectionsContainer>

      <LogoutButton onClick={handleLogout}>{t('modules_settings_pages_title_logout')}</LogoutButton>
    </Container>
  )

  const renderContent = (): ReactNode => {
    if (currentRoute === 'settings') {
      return <SettingsContent />
    }

    const page = pages[currentRoute]
    if (page) {
      const PageComponent = page.component
      return <PageComponent onBack={handleBack} />
    }

    return <SettingsContent />
  }

  const getPageTitle = (): string => {
    if (currentRoute === 'settings') {
      return t('modules_settings_pages_title')
    }

    const page = pages[currentRoute]
    return page ? page.title : t('modules_settings_pages_title')
  }

  return (
    <Modal
      title={getPageTitle()}
      showFooter={false}
      onClose={() => hideModal('settings')}
      showPrevious={currentRoute !== 'settings'}
      onPrevious={handleBack}
    >
      {renderContent()}
    </Modal>
  )
}

const SettingsItem = ({
  item,
  isLast,
  onNavigate,
}: {
  item: NavigationItem
  isLast: boolean
  onNavigate: (route: string) => void
}): JSX.Element => (
  <ItemWrapper>
    <ItemContainer
      onClick={() => {
        if (item.route) {
          onNavigate(item.route)
        } else if (item.onClick) {
          item.onClick()
        }
      }}
    >
      <ItemText>{item.title}</ItemText>
      <ItemIcon>
        <ArrowRightIcon />
      </ItemIcon>
    </ItemContainer>
    {!isLast && <Divider />}
  </ItemWrapper>
)
